import React, { useState, useCallback, useRef, useEffect } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'

// Set up the worker for react-pdf
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`

interface PDFViewerProps {
  src: string
}

export const PDFViewer: React.FC<PDFViewerProps> = ({ src }) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [numPages, setNumPages] = useState<number | null>(null)
  const [scale, setScale] = useState(1.0)
  const [containerWidth, setContainerWidth] = useState<number>(0)

  // Update container width on resize for responsive PDF
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.clientWidth)
      }
    }
    
    // Initial width
    updateWidth()
    
    window.addEventListener('resize', updateWidth)
    return () => window.removeEventListener('resize', updateWidth)
  }, [])

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages)
  }

  const toggleFullscreen = useCallback(() => {
    if (!containerRef.current) return

    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().then(() => {
        setIsFullscreen(true)
      }).catch((err) => {
        console.error('Error attempting to enable fullscreen:', err)
      })
    } else {
      document.exitFullscreen().then(() => {
        setIsFullscreen(false)
      })
    }
  }, [])

  // Calculate dynamic scale based on container width (assuming standard A4 aspect ratio width ~600px)
  const effectiveScale = containerWidth ? Math.min((containerWidth - 40) / 600, 1.5) * scale : scale

  return (
    <div 
      ref={containerRef} 
      className={`relative group bg-slate-900/90 rounded-2xl overflow-hidden flex flex-col ${isFullscreen ? 'h-screen w-screen rounded-none' : 'h-[70vh] sm:h-[80vh] min-h-[400px] sm:min-h-[600px] w-full'}`}
      onContextMenu={(e) => e.preventDefault()} // Prevent right-click save as
    >
      {/* Custom Toolbar */}
      <div className="absolute top-0 left-0 right-0 z-20 flex justify-between items-center px-4 py-3 bg-black/60 backdrop-blur-md border-b border-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        <div className="flex items-center gap-2">
            <span className="text-white text-xs font-bold uppercase tracking-widest bg-white/10 px-2 py-1 rounded">Secure PDF</span>
        </div>
        <div className="flex items-center gap-3">
          <button 
            onClick={() => setScale(s => Math.max(0.5, s - 0.2))}
            className="flex items-center justify-center size-8 rounded hover:bg-white/10 text-white transition-colors"
            title="Zoom Out"
          >
            <span className="material-symbols-outlined text-[20px]">zoom_out</span>
          </button>
          <span className="text-white text-xs font-mono">{Math.round(scale * 100)}%</span>
          <button 
            onClick={() => setScale(s => Math.min(3, s + 0.2))}
            className="flex items-center justify-center size-8 rounded hover:bg-white/10 text-white transition-colors"
            title="Zoom In"
          >
            <span className="material-symbols-outlined text-[20px]">zoom_in</span>
          </button>
          <div className="w-px h-6 bg-white/20 mx-1" />
          <button
            onClick={toggleFullscreen}
            className="flex items-center justify-center size-8 rounded hover:bg-white/10 text-white transition-colors"
            title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
          >
            <span className="material-symbols-outlined text-[20px]">
              {isFullscreen ? 'fullscreen_exit' : 'fullscreen'}
            </span>
          </button>
        </div>
      </div>

      {/* PDF Content */}
      <div className="flex-1 overflow-y-auto custom-scrollbar pt-14 pb-8 flex justify-center">
        <Document
          file={src}
          onLoadSuccess={onDocumentLoadSuccess}
          loading={
            <div className="flex flex-col items-center justify-center h-64 text-white">
                <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4" />
                <p className="text-sm">Loading secure document...</p>
            </div>
          }
          error={
            <div className="flex flex-col items-center justify-center h-64 text-rose-400">
                <span className="material-symbols-outlined text-4xl mb-2">error</span>
                <p className="text-sm font-bold">Failed to load document.</p>
                <p className="text-xs opacity-70 mt-1">Please try refreshing the page.</p>
            </div>
          }
        >
          {numPages && Array.from(new Array(numPages), (_el, index) => (
            <div key={`page_${index + 1}`} className="mb-4 shadow-2xl overflow-hidden rounded">
                <Page 
                    pageNumber={index + 1} 
                    scale={effectiveScale}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                    className="select-none" // Prevent text selection
                />
            </div>
          ))}
        </Document>
      </div>
    </div>
  )
}

export default PDFViewer
